import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, formatFecha, ordenarLista } from '../../utils/UtilesComun';
import BotonObservacionesLista from '../botones/BotonObservacionesLista';
import { BoxTitleApp, TableCellApp, TableModuleApp, VisibilidadIconApp } from '../custom/CustomComponents';
import ModalObservacionesJugador from '../jugador/ModalObservacionesJugador';
import ModalSeleccionarJugador from '../jugador/ModalSeleccionarJugador';

const columnsFull = [
  { field: 'timestamp', label: 'Fecha', width: '16%', tooltip: 'Fecha de la observación', sx: { pl: 1, pr: 0.5 } },
  { field: 'nombreJugador', label: 'Jugador', width: '46%', tooltip: 'Nombre del jugador', sx: { pl: 1, pr: 0.5 } },
  {
    field: 'visibilidad',
    label: 'Vis.',
    width: '6%',
    align: 'right',
    sortable: true,
    tooltip: 'Visibilidad',
    sx: { px: 0.25 },
  },
  {
    field: 'botonObservaciones',
    label: 'Observación',
    width: '32%',
    align: 'right',
    sortable: false,
    tooltip: 'Ver, editar o eliminar',
    sx: { pr: 1.5, pl: 0.5 },
  },
];

const columnsCompact = [
  { field: 'timestamp', label: 'Fecha', width: '16%', tooltip: 'Fecha de la observación', sx: { pl: 1, pr: 0.5 } },
  { field: 'nombreJugador', label: 'Jugador', width: '46%', tooltip: 'Nombre del jugador', sx: { pl: 0.5, pr: 0.5 } },
  {
    field: 'visibilidad',
    label: 'Vis.',
    width: '6%',
    align: 'right',
    sortable: true,
    tooltip: 'Visibilidad',
    sx: { px: 0.25 },
  },
  {
    field: 'botonObservaciones',
    label: 'Observación',
    width: '32%',
    align: 'right',
    sortable: false,
    tooltip: 'Ver, editar o eliminar',
    sx: { pr: 1.5, pl: 0.5 },
  },
];

const FilaObservacion = ({ observacion, columns, compact, onUpdate, onToggleVisibility, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [visibilidadAnchorEl, setVisibilidadAnchorEl] = useState(null);

  const handleVisibilityClick = (event) => {
    setVisibilidadAnchorEl(event.currentTarget);
  };

  const handleVisibilityClose = () => {
    setVisibilidadAnchorEl(null);
  };

  const handleVisibilityChange = (nueva) => {
    if (onToggleVisibility) {
      onToggleVisibility(observacion, nueva === 'PUBLICA');
    }
    handleVisibilityClose();
  };

  return (
    <TableRow hover>
      {columns.map((col) => {
        if (col.field === 'botonObservaciones') {
          return (
            <TableCellApp
              key={col.field}
              align="right"
              sx={{
                width: col.width,
                py: 0.6,
                pl: 0.5,
                pr: 1.5,
                textAlign: 'right',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: '100%',
                  gap: 0.75,
                }}
              >
                <BotonObservacionesLista
                  idJugador={observacion.idJugador}
                  observaciones={[observacion]}
                  badge={true}
                  badgeText="Ver observación"
                  showAddButton={false}
                  onUpdate={onUpdate}
                  previewText={null}
                />
                <Tooltip title="Editar observación" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEdit) {
                        onEdit(observacion);
                      }
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0,
                      color: '#64748b',
                      border: '1px solid #e2e8f0',
                      borderRadius: '5px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        color: '#0f766e',
                        backgroundColor: '#f0fdfa',
                        borderColor: '#99f6e4',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: '0.80rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar observación" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDelete) {
                        onDelete(observacion);
                      }
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0,
                      color: '#64748b',
                      border: '1px solid #e2e8f0',
                      borderRadius: '5px',
                      backgroundColor: '#f8fafc',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        color: '#ef4444',
                        backgroundColor: '#fef2f2',
                        borderColor: '#fca5a5',
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'visibilidad') {
          return (
            <TableCellApp
              key={col.field}
              align="right"
              sx={{
                width: col.width,
                py: 0.6,
                px: 0.25,
                textAlign: 'right',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                <Tooltip title="Visibilidad: Clic para cambiar" arrow>
                  <IconButton
                    size="small"
                    onClick={handleVisibilityClick}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      width: 22,
                      height: 22,
                      p: 0,
                      minWidth: 22,
                      margin: 0,
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'primary.main',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '0.78rem',
                        color: 'text.secondary',
                      },
                    }}
                  >
                    <VisibilidadIconApp
                      visibilidad={observacion.publica ? 'PUBLICA' : 'PRIVADA'}
                      sx={{ fontSize: '0.78rem', color: 'text.secondary' }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <Menu anchorEl={visibilidadAnchorEl} open={Boolean(visibilidadAnchorEl)} onClose={handleVisibilityClose}>
                <MenuItem onClick={() => handleVisibilityChange('PRIVADA')} sx={{ fontSize: '0.8rem' }}>
                  <LockIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 1 }} /> Privada
                </MenuItem>
                <MenuItem onClick={() => handleVisibilityChange('PUBLICA')} sx={{ fontSize: '0.8rem' }}>
                  <PublicIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 1 }} /> Pública
                </MenuItem>
              </Menu>
            </TableCellApp>
          );
        }

        if (col.field === 'nombreJugador') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                width: col.width,
                py: 0.6,
                pl: compact ? 0.5 : 1,
                pr: 0.5,
                overflow: 'hidden',
              }}
            >
              <Tooltip title={capitalizar(observacion.nombreJugador)} arrow placement="top">
                <Box
                  onClick={() => navigate(`/jugador/${observacion.idJugador}`)}
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    fontSize: '0.80rem',
                    color: 'text.primary',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {capitalizar(observacion.nombreJugador)}
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        if (col.field === 'timestamp') {
          const { completa } = formatFecha(observacion.timestamp);
          const fechaGuiones = completa ? completa.replace(/\//g, '-') : '-';
          return (
            <TableCellApp
              key={col.field}
              sx={{
                width: col.width,
                py: 0.6,
                pl: 1,
                pr: 0.5,
                textAlign: 'left',
                whiteSpace: 'nowrap',
              }}
            >
              <Tooltip title={fechaGuiones} arrow>
                <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.78rem', fontWeight: 500 }}>
                  {fechaGuiones}
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        return (
          <TableCellApp key={col.field} sx={{ width: col.width, py: 0.6 }}>
            {observacion[col.field]}
          </TableCellApp>
        );
      })}
    </TableRow>
  );
};

const ObservacionesUsuario = ({
  observaciones,
  onUpdate,
  compact = false,
  showCreateButton = true,
  equiposClub = null,
  title = 'Mis Observaciones',
}) => {
  const { editarObservacion, eliminarObservacion, obtenerObservacionesJugador } = useContext(Context);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(compact ? 5 : 10);
  const [sort, setSort] = useState({ field: 'timestamp', direction: 'desc' });

  // Estados para selección y creación de observación
  const [selectorJugadorOpen, setSelectorJugadorOpen] = useState(false);
  const [modalObsOpen, setModalObsOpen] = useState(false);
  const [jugadorSeleccionadoObs, setJugadorSeleccionadoObs] = useState(null);

  // Estados para modal de edición
  const [obsEditando, setObsEditando] = useState(null);
  const [textoEditando, setTextoEditando] = useState('');
  const [publicaEditando, setPublicaEditando] = useState(false);

  // Estados para modal de confirmación de eliminación
  const [obsEliminando, setObsEliminando] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const columns = compact ? columnsCompact : columnsFull;

  const handleSelectJugadorParaCrear = async (jugador) => {
    setSelectorJugadorOpen(false);
    const idJug = jugador.idJugador;
    const nomJug = jugador.nombreJugador || jugador.nombre || 'Jugador';
    let obsList = [];
    try {
      obsList = await obtenerObservacionesJugador(idJug);
    } catch {
      obsList = [];
    }
    setJugadorSeleccionadoObs({
      idJugador: idJug,
      nombre: nomJug,
      observaciones: obsList || [],
    });
    setModalObsOpen(true);
  };

  const handleToggleVisibility = async (obs, nuevaVisibilidad) => {
    const updatedObservaciones = await editarObservacion(obs.idObservacion, { ...obs, publica: nuevaVisibilidad });
    if (onUpdate && updatedObservaciones) {
      onUpdate(obs.idJugador, updatedObservaciones);
    }
  };

  const handleOpenEdit = (obs) => {
    setObsEditando(obs);
    setTextoEditando(obs.observacion || '');
    setPublicaEditando(Boolean(obs.publica));
  };

  const handleSaveEdit = async () => {
    if (!obsEditando || !textoEditando.trim() || guardando) {
      return;
    }
    setGuardando(true);
    try {
      const updated = await editarObservacion(obsEditando.idObservacion, {
        ...obsEditando,
        observacion: textoEditando.trim(),
        publica: publicaEditando,
      });
      if (onUpdate && updated) {
        onUpdate(obsEditando.idJugador, updated);
      }
      setObsEditando(null);
    } catch (err) {
      console.error('Error al editar observación:', err);
    } finally {
      setGuardando(false);
    }
  };

  const handleOpenDelete = (obs) => {
    setObsEliminando(obs);
  };

  const handleConfirmDelete = async () => {
    if (!obsEliminando || guardando) {
      return;
    }
    setGuardando(true);
    try {
      const updated = await eliminarObservacion(obsEliminando.idObservacion);
      if (onUpdate) {
        onUpdate(obsEliminando.idJugador, updated || []);
      }
      setObsEliminando(null);
    } catch (err) {
      console.error('Error al eliminar observación:', err);
    } finally {
      setGuardando(false);
    }
  };

  const sortedObservaciones = useMemo(() => {
    return ordenarLista(observaciones, sort);
  }, [observaciones, sort]);

  if (!observaciones) {
    return null;
  }

  return (
    <>
      {title && (
        <BoxTitleApp
          action={
            showCreateButton && (
              <Tooltip title="Crear observación" arrow placement="top">
                <Button
                  size="small"
                  onClick={() => setSelectorJugadorOpen(true)}
                  startIcon={<AddIcon sx={{ fontSize: '0.95rem !important' }} />}
                  sx={{
                    height: 26,
                    px: 1.25,
                    borderRadius: '13px',
                    backgroundColor: '#0f766e',
                    color: '#ffffff',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 1px 2px rgba(15, 118, 110, 0.2)',
                    '&:hover': {
                      backgroundColor: '#0d9488',
                      boxShadow: '0 2px 5px rgba(15, 118, 110, 0.3)',
                    },
                  }}
                >
                  Crear
                </Button>
              </Tooltip>
            )
          }
        >
          {title}
        </BoxTitleApp>
      )}

      <TableModuleApp
        noHeader
        noBorder
        columns={columns}
        sort={sort}
        setSort={setSort}
        data={sortedObservaciones.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
        renderRow={(obs) => (
          <FilaObservacion
            key={obs.idObservacion}
            observacion={obs}
            columns={columns}
            compact={compact}
            onUpdate={onUpdate}
            onToggleVisibility={handleToggleVisibility}
            onEdit={handleOpenEdit}
            onDelete={handleOpenDelete}
          />
        )}
        hasPagination
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        rowsPerPageOptions={compact ? [5, 10, 20] : [10, 25, 50]}
        totalCount={sortedObservaciones.length}
      />

      {/* Diálogo para Editar Observación */}
      <Dialog
        open={Boolean(obsEditando)}
        onClose={() => !guardando && setObsEditando(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
          Editar observación - {obsEditando ? capitalizar(obsEditando.nombreJugador) : ''}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            size="small"
            placeholder="Escribe la observación sobre el jugador..."
            value={textoEditando}
            onChange={(e) => setTextoEditando(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&.Mui-focused fieldset': {
                  borderColor: '#0f766e',
                },
              },
            }}
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilidadIconApp
                visibilidad={publicaEditando ? 'PUBLICA' : 'PRIVADA'}
                sx={{ fontSize: '1.1rem', color: 'text.secondary' }}
              />
              <Typography sx={{ fontSize: '0.84rem', color: '#334155', fontWeight: 600 }}>
                {publicaEditando ? 'Visible para todo el club' : 'Privada (solo tú)'}
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={() => setPublicaEditando((prev) => !prev)}
              sx={{ textTransform: 'none', fontSize: '0.78rem' }}
            >
              Cambiar a {publicaEditando ? 'Privada' : 'Pública'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setObsEditando(null)}
            disabled={guardando}
            sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.82rem' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!textoEditando.trim() || guardando}
            onClick={handleSaveEdit}
            sx={{
              backgroundColor: '#0f766e',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              '&:hover': { backgroundColor: '#0d9488' },
            }}
          >
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmación para Eliminar Observación */}
      <Dialog
        open={Boolean(obsEliminando)}
        onClose={() => !guardando && setObsEliminando(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
          Eliminar observación
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#475569' }}>
            ¿Estás seguro de que deseas eliminar esta observación del jugador{' '}
            <strong>{obsEliminando ? capitalizar(obsEliminando.nombreJugador) : ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setObsEliminando(null)}
            disabled={guardando}
            sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.82rem' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={guardando}
            onClick={handleConfirmDelete}
            sx={{
              backgroundColor: '#ef4444',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              '&:hover': { backgroundColor: '#dc2626' },
            }}
          >
            {guardando ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ModalSeleccionarJugador
        open={selectorJugadorOpen}
        onClose={() => setSelectorJugadorOpen(false)}
        onSelectJugador={handleSelectJugadorParaCrear}
        title="Crear observación - Seleccionar jugador"
        equiposClub={equiposClub}
      />

      {modalObsOpen && jugadorSeleccionadoObs && (
        <ModalObservacionesJugador
          open={modalObsOpen}
          onClose={() => {
            setModalObsOpen(false);
            setJugadorSeleccionadoObs(null);
          }}
          idJugador={jugadorSeleccionadoObs.idJugador}
          nombreJugador={jugadorSeleccionadoObs.nombre}
          observaciones={jugadorSeleccionadoObs.observaciones}
          initialCreateMode={true}
          onUpdate={(updated) => {
            if (onUpdate) {
              onUpdate(jugadorSeleccionadoObs.idJugador, updated);
            }
            setModalObsOpen(false);
            setJugadorSeleccionadoObs(null);
          }}
        />
      )}
    </>
  );
};

ObservacionesUsuario.propTypes = {
  observaciones: PropTypes.array,
  onUpdate: PropTypes.func,
  compact: PropTypes.bool,
  showCreateButton: PropTypes.bool,
  equiposClub: PropTypes.array,
};

FilaObservacion.propTypes = {
  observacion: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  compact: PropTypes.bool,
  onUpdate: PropTypes.func,
  onToggleVisibility: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default ObservacionesUsuario;
